class SearchManager {
  static instances = new Map();
  static scrollLocked = false;

  static enhanceAsSearchTrigger(element, context = "sticky") {
    if (!element) return false;

    if (element.classList.contains("fakeTrigger-enhanced")) return true;

    element.classList.add("fakeTrigger-enhanced");
    element.style.cursor = "pointer";
    element.style.zIndex = "100";
    element.style.position = "relative";

    const clickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const instance = SearchManager.instances.get(context);
      if (instance) {
        const originalTrigger = instance.fakeTrigger;
        instance.fakeTrigger = element;
        
        instance.activate(e);
        instance.fakeTrigger = originalTrigger;
      }
    };

    element.removeEventListener("click", clickHandler);
    element.addEventListener("click", clickHandler);

    return true;
  }

  static enhanceAllTriggersIn(container, context = "sticky") {
    if (!container) return;

    const triggers = container.querySelectorAll(
      ".search-fake-trigger, .header-search-form__inner"
    );
    triggers.forEach((trigger) =>
      SearchManager.enhanceAsSearchTrigger(trigger, context)
    );

    return triggers.length;
  }

  constructor(context = "main") {
    this.context = context;
    this.state = {
      isInitialized: false,
      isActive: false,
      hasError: false,
      errorMessage: null,
      searchFormReady: false,
      predictiveScriptLoaded: false,
    };
    this.clone = null;
    this.scrollPosition = 0;
    this.resizeObserver = null;
    this.animationFrame = null;
    this.init();
    SearchManager.instances.set(context, this);
  }

  setState(newState) {
    this.state = { ...this.state, ...newState };
    this.handleStateChange();
  }

  handleStateChange() {
    if (this.state.hasError) {
      this.handleError();
    }
  }

  async loadPredictiveSearchScript() {
    try {
      if (document.body.classList.contains("predictive-script-loaded")) {
        this.setState({ predictiveScriptLoaded: true });
        return;
      }

      const script = document.createElement("script");
      script.src = KROWN.settings.predictive_search_script;

      await new Promise((resolve, reject) => {
        script.onload = () => {
          document.body.classList.add("predictive-script-loaded");
          this.setState({ predictiveScriptLoaded: true });
          resolve();
        };
        script.onerror = () => {
          reject(new Error("Failed to load predictive search script"));
        };
        document.head.appendChild(script);
      });
    } catch (error) {
      this.setState({
        hasError: true,
        errorMessage: "Failed to load search script",
      });
      throw error;
    }
  }

  async waitForSearchForm() {
    try {
      if (!this.state.predictiveScriptLoaded) {
        await this.loadPredictiveSearchScript();
      }

      await new Promise((resolve, reject) => {
        if (customElements.get("search-form")) {
          resolve();
          return;
        }

        const timeout = setTimeout(() => {
          reject(new Error("SearchForm initialization timeout"));
        }, 10000); // Aumentato a 10 secondi

        const checkInterval = setInterval(() => {
          if (customElements.get("search-form")) {
            clearInterval(checkInterval);
            clearTimeout(timeout);
            resolve();
          }
        }, 100);

        const handleSearchFormReady = () => {
          clearInterval(checkInterval);
          clearTimeout(timeout);
          document.removeEventListener(
            "search-form-ready",
            handleSearchFormReady
          );
          resolve();
        };
        document.addEventListener("search-form-ready", handleSearchFormReady);
      });

      this.setState({ searchFormReady: true });
    } catch (error) {
      this.setState({
        hasError: true,
        errorMessage: "Failed to initialize SearchForm",
      });
      throw error;
    }
  }

  async initializeSearchForm(searchForm) {
    try {
      if (!customElements.get("search-form")) {
        throw new Error("SearchForm component non è ancora definito.");
      }

      
      customElements.upgrade(searchForm);

     
      if (
        typeof searchForm.init === "function" &&
        (typeof searchForm.isInitialized === "undefined" ||
          !searchForm.isInitialized)
      ) {
        searchForm.init();
      }

     
      await new Promise((resolve) => setTimeout(resolve, 150)); 

      this.setState({ isInitialized: true });
    } catch (error) {
      this.setState({
        hasError: true,
        errorMessage: `Errore in initializeSearchForm: ${error.message}`,
      });
      throw error; // Rilancia l'errore per far scattare handleError() nel chiamante
    }
  }

  async setupPredictiveResults() {
    try {
      const searchSuggestions = this.clone.querySelector(".search-suggestions");
      if (!searchSuggestions) {
        throw new Error("Search suggestions container not found");
      }

      const resultsContainer = searchSuggestions.querySelector(
        ".search-results-container"
      );
      if (!resultsContainer) {
        const newContainer = document.createElement("div");
        newContainer.className = "search-results-container";
        searchSuggestions.appendChild(newContainer);
      }

      let predictiveResults = this.clone.querySelector(
        "[data-js-search-results]"
      );
      if (!predictiveResults) {
        predictiveResults = document.createElement("div");
        predictiveResults.setAttribute("data-js-search-results", "");
        predictiveResults.className = "predictive-search-results";
        resultsContainer.appendChild(predictiveResults);
      }
    } catch (error) {
      this.setState({
        hasError: true,
        errorMessage: "Failed to setup predictive results",
      });
      throw error;
    }
  }

  async prepareClone() {
    try {
      if (!this.template) {
        if (this.context === "sticky") {
          const mainTemplate = document.getElementById("search-full-template");
          if (mainTemplate) {
            this.template = mainTemplate;
          } else {
            throw new Error("Nessun template trovato per la ricerca");
          }
        } else {
          throw new Error("Template not found");
        }
      }

      this.clone = this.template.content.cloneNode(true).firstElementChild;

      this.clone.classList.add(
        "header-search-wrapper--clone",
        `context-${this.context}`
      );

      document.body.appendChild(this.clone);

      if (this.context === "sticky") {
        const stickyHeader = document.querySelector(".sticky-header");

        if (
          stickyHeader &&
          stickyHeader.querySelector(".element--hide-on-portable-plus")
        ) {
          this.clone.classList.add("portable-plus-override");
        }

        if (
          stickyHeader &&
          stickyHeader.querySelector(
            ".site-header.element--hide-on-portable-plus"
          )
        ) {
          this.clone.classList.add("site-header-portable-override");
        }

        if (
          this.fakeTrigger &&
          this.fakeTrigger.closest(".element--hide-on-portable-plus")
        ) {
          this.clone.classList.add("trigger-in-hidden-element");
        }
      }

      const searchForm = this.clone.querySelector("search-form");
      if (!searchForm) {
        throw new Error("SearchForm not found in clone");
      }

      await this.initializeSearchForm(searchForm);
      await this.setupPredictiveResults();
      this.addCloseButton();
    } catch (error) {
      this.setState({
        hasError: true,
        errorMessage: "Failed to prepare clone",
      });
      throw error;
    }
  }

  addCloseButton() {
    if (!this.clone.querySelector(".header-search__close")) {
      const closeButton = document.createElement("button");
      closeButton.className = "header-search__close";
      closeButton.setAttribute("aria-label", "Chiudi ricerca");
      closeButton.addEventListener("click", () => this.deactivate());
      this.clone.appendChild(closeButton);
    }
  }

  handleError() {
    if (this.clone && this.clone.parentElement) {
      this.clone.parentElement.removeChild(this.clone);
    }

    this.clone = null;
    if (this.fakeTrigger) {
      this.fakeTrigger.style.opacity = "";
      this.fakeTrigger.style.pointerEvents = "";
    }
  }

  init() {
    this.initElements();
    this.initEvents();
    this.checkStickyHeader();

    if (this.context === "sticky") {
      if (this.fakeTrigger && !this.fakeTrigger.hasAttribute("aria-expanded")) {
        this.fakeTrigger.setAttribute("aria-expanded", "false");
      }

      if (this.fakeTrigger) {
        this.fakeTrigger.style.cursor = "pointer";

        this.originalTriggerStyles = {
          opacity: this.fakeTrigger.style.opacity,
          pointerEvents: this.fakeTrigger.style.pointerEvents,
          display: this.fakeTrigger.style.display,
          visibility: this.fakeTrigger.style.visibility,
        };
      }

      this.setupHiddenTriggerMonitoring();
    }
  }

  initElements() {
    const stickySelector = this.context === "sticky" ? ".sticky-header " : "";

    let possibleSelectors = [
      `${stickySelector}.search-fake-trigger`,
      `${stickySelector}.area--search .header-search-form__inner`,
      `${stickySelector}.area--search .search-fake-trigger`,
      `${stickySelector} .search-fake-trigger`,
      `${stickySelector} .header-search-form__inner`,
      `${stickySelector}.site-header.element--hide-on-portable-plus .search-fake-trigger`,
      `${stickySelector}.site-header.element--hide-on-portable-plus .header-search-form__inner`,
      `${stickySelector} .site-header.element--hide-on-portable-plus .search-fake-trigger`,
      `${stickySelector} .site-header.element--hide-on-portable-plus .header-search-form__inner`,
      `${stickySelector} .element--hide-on-portable-plus .search-fake-trigger`,
      `${stickySelector} .element--hide-on-portable-plus .header-search-form__inner`,
    ];

    for (let selector of possibleSelectors) {
      this.fakeTrigger = document.querySelector(selector);
      if (this.fakeTrigger) {
        break;
      }
    }

    // Caso specifico: controlla esplicitamente per il trigger nell'elemento con classe "site-header element--hide-on-portable-plus"
    if (!this.fakeTrigger && this.context === "sticky") {
      const stickyHeader = document.querySelector(".sticky-header");
      if (stickyHeader) {
        const hiddenSiteHeader = stickyHeader.querySelector(
          ".site-header.element--hide-on-portable-plus"
        );
        if (hiddenSiteHeader) {
          const hiddenTrigger =
            hiddenSiteHeader.querySelector(".search-fake-trigger") ||
            hiddenSiteHeader.querySelector(".header-search-form__inner");

          if (hiddenTrigger) {
            this.fakeTrigger = hiddenTrigger;
          }
        }
      }
    }

    if (!this.fakeTrigger && this.context === "sticky") {
      const stickyHeader = document.querySelector(".sticky-header");
      if (stickyHeader) {
        this.fakeTrigger =
          stickyHeader.querySelector(".search-fake-trigger") ||
          stickyHeader.querySelector(".header-search-form__inner");
      }

      if (!this.fakeTrigger) {
        const mainTrigger = document.querySelector(
          ".header-search-form__inner"
        );
        if (mainTrigger) {
          this.fakeTrigger = mainTrigger;
        }
      }
    }

    if (this.fakeTrigger && !this.fakeTrigger.hasAttribute("aria-expanded")) {
      this.fakeTrigger.setAttribute("aria-expanded", "false");
    }

    this.template = document.getElementById(
      `search-full-template${this.context === "sticky" ? "-sticky" : ""}`
    );

    if (!this.template && this.context === "sticky") {
      this.template = document.getElementById("search-full-template");
    }

    this.overlay = document.getElementById("global-search-overlay");
    this.stickyHeader = document.querySelector(".sticky-header");
  }

  initEvents() {
    
    if (this.fakeTrigger && this._clickHandler) {
      this.fakeTrigger.removeEventListener("click", this._clickHandler);
    }

    this._clickHandler = (e) => {
      this.activate(e);
    };

    if (this.fakeTrigger) {
      this.fakeTrigger.addEventListener("click", this._clickHandler);

      if (
        this.fakeTrigger.closest(".element--hide-on-portable-plus") ||
        this.fakeTrigger.closest(".site-header.element--hide-on-portable-plus")
      ) {
        this.fakeTrigger.style.cursor = "pointer";
        this.fakeTrigger.style.zIndex = "10";
        this.fakeTrigger.classList.add("fakeTrigger-enhanced");
      }
    }

    if (this.context === "sticky") {
      const stickyHeader = document.querySelector(".sticky-header");
      if (stickyHeader) {
        
        if (this._delegatedHandler) {
          stickyHeader.removeEventListener("click", this._delegatedHandler);
        }

        
        this._delegatedHandler = (e) => {
          const clickedTrigger = e.target.closest(
            ".search-fake-trigger, .header-search-form__inner"
          );
          if (clickedTrigger) {
            if (this.fakeTrigger !== clickedTrigger) {
              const originalTrigger = this.fakeTrigger;
              this.fakeTrigger = clickedTrigger;

              this.activate(e);

              this.fakeTrigger = originalTrigger;
            } else {
              this.activate(e);
            }
          }
        };

        stickyHeader.addEventListener("click", this._delegatedHandler);
      }
    }

    this.overlay?.addEventListener("click", () => this.deactivate());
    document.addEventListener("keydown", (e) => this.handleKeyDown(e));
  }

  checkStickyHeader() {
    if (this.context === "sticky" && this.stickyHeader) {
      this.resizeObserver = new ResizeObserver(() => this.positionClone());
      this.resizeObserver.observe(this.stickyHeader);
    }
  }

  handleKeyDown(e) {
    if (e.key === "Escape") this.deactivate();
    if (e.key === "Tab" && this.state.isActive) this.trapFocus(e);
  }

  trapFocus(e) {
    const focusable = this.clone.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  async activate(e) {
    if (e) {
      e.preventDefault();
    }

    if (this.state.isActive) {
      return;
    }

    const isFromHiddenSiteHeader =
      this.fakeTrigger &&
      (this.fakeTrigger.closest(
        ".site-header.element--hide-on-portable-plus"
      ) ||
        this.fakeTrigger.classList.contains("fakeTrigger-enhanced"));

    try {
      if (!this.state.searchFormReady) {
        await this.waitForSearchForm(); 
      }

      if (this.fakeTrigger) {
        this.originalTriggerStyles = {
          opacity: this.fakeTrigger.style.opacity,
          pointerEvents: this.fakeTrigger.style.pointerEvents,
          display: this.fakeTrigger.style.display,
          visibility: this.fakeTrigger.style.visibility,
        };
        this.fakeTrigger.style.opacity = "0";
        this.fakeTrigger.style.visibility = "hidden";
        this.fakeTrigger.style.display = "none";
      }

      
      SearchManager.instances.forEach((instance) => {
        if (instance !== this && instance.state.isActive) {
          instance.deactivate();
        }
      });

      await this.prepareClone();

      
      this.lockScroll();
      this.addScreenSizeClasses(); 

      
      
      if (this.clone) {
        this.clone.classList.add("active", "clone-positioned");
        const input = this.clone.querySelector("input");
        if (input) {
          input.focus({ preventScroll: true });
        }
      } else {
        throw new Error("Clone non disponibile dopo la preparazione.");
      }

      if (this.overlay) {
        this.overlay.classList.add("active");
      } else {
        
        if (
          isFromHiddenSiteHeader &&
          !document.getElementById("global-search-overlay")
        ) {
          const newOverlay = document.createElement("div");
          newOverlay.id = "global-search-overlay";
          newOverlay.className = "active";
          newOverlay.addEventListener("click", () => this.deactivate());
          document.body.appendChild(newOverlay);
          this.overlay = newOverlay;
        }
      }

      if (this.fakeTrigger) {
        this.fakeTrigger.setAttribute("aria-expanded", "true");
      }

      this.setState({ isActive: true });
      document.body.classList.add("search-active");
    } catch (error) {
      this.setState({ hasError: true, errorMessage: error.message });
      this.handleError(); 
    }
  }

  addScreenSizeClasses() {
    if (!this.clone) return;

    this.clone.classList.remove(
      "screen-mobile",
      "screen-tablet",
      "screen-desktop"
    );

    const screenWidth = window.innerWidth;

    if (screenWidth <= 766) {
      this.clone.classList.add("screen-mobile");
    } else if (screenWidth > 766 && screenWidth < 1092) {
      this.clone.classList.add("screen-tablet");
    } else {
      this.clone.classList.add("screen-desktop");
    }
  }

  positionClone() {
    if (!this.state.isActive || !this.clone) return;

    this.addScreenSizeClasses();
  }

  applyFallbackPosition() {
    if (!this.clone) return;

    this.addScreenSizeClasses();
  }

  lockScroll() {
    if (SearchManager.scrollLocked) return;

    try {
      this.scrollPosition = window.scrollY || window.pageYOffset;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = "100%";

      SearchManager.scrollLocked = true;
    } catch (error) {
      this.unlockScrollSafe();
    }
  }

  unlockScrollSafe() {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";

    SearchManager.scrollLocked = false;

    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
  }

  unlockScroll() {
    if (!SearchManager.scrollLocked) return;

    try {
      const scrollY = Math.abs(parseInt(document.body.style.top || "0"));

      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      SearchManager.scrollLocked = false;

      setTimeout(() => {
        window.scrollTo(0, scrollY);
      }, 10);
    } catch (error) {
      this.unlockScrollSafe();
    }
  }

  deactivate() {
    if (!this.state.isActive) {
      return;
    }

    if (this.clone) {
      this.clone.classList.remove("active");
    }

    if (this.overlay) {
      this.overlay.classList.remove("active");
    }

    this.ensureTriggerVisibility();

    this.unlockScroll();

    if (this.context === "sticky" && window.innerWidth < 1024) {
      const stickyHeader = document.querySelector(".sticky-header");
      if (stickyHeader) {
        
        const tempVisibleElements = stickyHeader.querySelectorAll(
          "[data-was-hidden='true']"
        );

        tempVisibleElements.forEach((elem) => {
          elem.style.display = "";
          delete elem.dataset.wasHidden;
        });

        const tempVisibleTriggers = stickyHeader.querySelectorAll(
          "[data-was-hidden-trigger='true']"
        );

        tempVisibleTriggers.forEach((trigger) => {
          trigger.style.display = "";
          trigger.style.visibility = "";
          trigger.style.opacity = "";
          trigger.setAttribute("aria-expanded", "false");
          delete trigger.dataset.wasHiddenTrigger;
        });
      }
    }

    this.setState({ isActive: false });
    document.body.classList.remove("search-active");

    if (this.clone) {
      const handleTransitionEnd = () => {
        if (this.clone && this.clone.parentElement) {
          this.clone.remove();
          this.clone = null; 

          this.ensureTriggerVisibility();
        }
      };

      const transitionTimeout = setTimeout(() => {
        if (this.clone && this.clone.parentElement) {
          handleTransitionEnd();
        }
      }, 300); 

      this.clone.addEventListener(
        "transitionend",
        () => {
          clearTimeout(transitionTimeout);
          handleTransitionEnd();
        },
        { once: true }
      );
    }

    setTimeout(() => this.ensureTriggerVisibility(), 500);
  }

  ensureTriggerVisibility() {
    const triggerSelector =
      this.context === "sticky"
        ? ".sticky-header .search-fake-trigger, .sticky-header .header-search-form__inner"
        : ".search-fake-trigger, .header-search-form__inner";

    const potentialTriggers = document.querySelectorAll(triggerSelector);

    potentialTriggers.forEach((trigger) => {
      trigger.style.opacity = "1";
      trigger.style.visibility = "visible";
      trigger.style.display = "";
    });

    if (this.fakeTrigger) {
      this.fakeTrigger.setAttribute("aria-expanded", "false");

      const computedStyle = window.getComputedStyle(this.fakeTrigger);

      let correctDisplay = "inline-flex"; 

      if (
        this.originalTriggerStyles &&
        this.originalTriggerStyles.display &&
        this.originalTriggerStyles.display !== "none"
      ) {
        correctDisplay = this.originalTriggerStyles.display;
      } else if (
        this.fakeTrigger.classList.contains("header-search-form__inner")
      ) {
        correctDisplay = "inline-flex"; 
      }

      const id = `trigger-fix-${Date.now()}`;
      this.fakeTrigger.setAttribute("data-fix-id", id);

      const styleEl = document.createElement("style");
      styleEl.id = `style-${id}`;
      styleEl.textContent = `
        [data-fix-id="${id}"] {
          opacity: 1 !important;
          visibility: visible !important;
          display: ${correctDisplay} !important;
          transition: none !important; /* Previene animazioni indesiderate */
        }
        [data-fix-id="${id}"] svg,
        [data-fix-id="${id}"] input {
          transition: none !important; /* Previene animazioni degli elementi interni */
          position: relative !important; /* Mantiene il posizionamento corretto */
        }
      `;
      document.head.appendChild(styleEl);

      void this.fakeTrigger.offsetWidth;

      setTimeout(() => {
        const styleToRemove = document.getElementById(`style-${id}`);
        if (styleToRemove) {
          styleToRemove.remove();
        }

        if (this.fakeTrigger) {
          this.fakeTrigger.removeAttribute("data-fix-id");
        }
      }, 1000);
    }
  }

  destroy() {
    this.deactivate();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.animationFrame);

    // Pulisci anche l'intervallo di monitoraggio
    if (this._hiddenTriggerInterval) {
      clearInterval(this._hiddenTriggerInterval);
      this._hiddenTriggerInterval = null;
    }

    SearchManager.instances.delete(this.context);
  }

  removeClone() {
    if (this.clone && this.clone.parentElement) {
      this.clone.parentElement.removeChild(this.clone);
    }

    this.clone = null;
    this.fakeTrigger.style.opacity = "";
    this.fakeTrigger.style.pointerEvents = "";

    document.removeEventListener("keydown", this.handleKeyDown.bind(this));
  }

  setupHiddenTriggerMonitoring() {
    const stickyHeader = document.querySelector(".sticky-header");
    if (!stickyHeader) return;

    const hiddenSiteHeader = stickyHeader.querySelector(
      ".site-header.element--hide-on-portable-plus"
    );
    if (!hiddenSiteHeader) return;

    this.addHiddenTriggerStyles();

    
    this._hiddenTriggerInterval = setInterval(() => {
      const hiddenTriggers = hiddenSiteHeader.querySelectorAll(
        ".search-fake-trigger, .header-search-form__inner"
      );

      if (hiddenTriggers.length > 0) {
        hiddenTriggers.forEach((trigger) => {
          if (!trigger.classList.contains("fakeTrigger-enhanced")) {
            
            trigger.style.cursor = "pointer";
            trigger.style.zIndex = "10";
            trigger.classList.add("fakeTrigger-enhanced");

            const handler = (e) => {
              e.preventDefault();
              e.stopPropagation();

              const originalTrigger = this.fakeTrigger;
              this.fakeTrigger = trigger;
              this.activate(e);
              this.fakeTrigger = originalTrigger;
            };

            trigger.removeEventListener("click", handler);
            trigger.addEventListener("click", handler);
          }
        });
      }
    }, 1000);
  }

  addHiddenTriggerStyles() {
    if (document.getElementById("hidden-trigger-styles")) return;

    const styleEl = document.createElement("style");
    styleEl.id = "hidden-trigger-styles";
    styleEl.textContent = `
      .fakeTrigger-enhanced {
        position: relative !important;
        cursor: pointer !important;
        z-index: 100 !important;
      }
      
      /* Versione con fallback per interazione touch */
      @media (max-width: 1024px) {
        .sticky-header .site-header.element--hide-on-portable-plus .search-fake-trigger::after,
        .sticky-header .site-header.element--hide-on-portable-plus .header-search-form__inner::after,
        .sticky-header .element--hide-on-portable-plus .search-fake-trigger::after,
        .sticky-header .element--hide-on-portable-plus .header-search-form__inner::after {
          content: "";
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          z-index: 99;
        }
      }
      
      /* Stili per l'overlay */
      #global-search-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9998;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      
      #global-search-overlay.active {
        opacity: 1;
        visibility: visible;
      }
      
      /* Stile specifico per il clone da trigger nascosto */
      .header-search-wrapper--clone.from-hidden-site-header {
        z-index: 9999 !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
    `;

    document.head.appendChild(styleEl);
  }
}

// Initialization with MutationObserver for dynamic content
document.addEventListener("DOMContentLoaded", () => {
  const initSearchManager = () => {
    if (!SearchManager.instances.has("main")) {
      new SearchManager("main");
    }

    if (
      document.querySelector(".sticky-header") &&
      !SearchManager.instances.has("sticky")
    ) {
      const manager = new SearchManager("sticky");
    }

    const stickyHeader = document.querySelector(".sticky-header");
    if (stickyHeader) {
      const hiddenSiteHeader = stickyHeader.querySelector(
        ".site-header.element--hide-on-portable-plus"
      );
      if (hiddenSiteHeader) {
        const count = SearchManager.enhanceAllTriggersIn(
          hiddenSiteHeader,
          "sticky"
        );
      }

      const hiddenElements = stickyHeader.querySelectorAll(
        ".element--hide-on-portable-plus"
      );
      hiddenElements.forEach((element) => {
        const count = SearchManager.enhanceAllTriggersIn(element, "sticky");
      });
    }
  };

  initSearchManager();

  setTimeout(() => {
    const stickyHeader = document.querySelector(".sticky-header");

    if (!SearchManager.instances.has("sticky") && stickyHeader) {
      const manager = new SearchManager("sticky");
    }

    const hiddenSiteHeader = stickyHeader?.querySelector(
      ".site-header.element--hide-on-portable-plus"
    );
    if (hiddenSiteHeader) {
      SearchManager.enhanceAllTriggersIn(hiddenSiteHeader, "sticky");
    }

    const hiddenElements = stickyHeader?.querySelectorAll(
      ".element--hide-on-portable-plus"
    );
    if (hiddenElements) {
      hiddenElements.forEach((element) => {
        SearchManager.enhanceAllTriggersIn(element, "sticky");
      });
    }
  }, 1000);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        let stickyHeaderFound = false;

        for (const node of mutation.addedNodes) {
          if (node.classList && node.classList.contains("sticky-header")) {
            stickyHeaderFound = true;

            setTimeout(() => {
              if (!SearchManager.instances.has("sticky")) {
                const manager = new SearchManager("sticky");
              }

  
              const hiddenSiteHeader = node.querySelector(
                ".site-header.element--hide-on-portable-plus"
              );
              if (hiddenSiteHeader) {
                SearchManager.enhanceAllTriggersIn(hiddenSiteHeader, "sticky");
              }

              const hiddenElements = node.querySelectorAll(
                ".element--hide-on-portable-plus"
              );
              hiddenElements.forEach((element) => {
                SearchManager.enhanceAllTriggersIn(element, "sticky");
              });
            }, 300);
          }
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  
  let resizeTimeout;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      
      const instances = Array.from(SearchManager.instances.entries());
      instances.forEach(([context, instance]) => {
        if (instance.state.isActive) {
      
          instance.addScreenSizeClasses();
        }
      });
    }, 100);
  });
});

window.addEventListener("beforeunload", () => {
  Array.from(SearchManager.instances.values()).forEach((instance) => {
    if (instance.state.isActive) {
      instance.unlockScrollSafe();
    }
  });

  if (SearchManager.scrollLocked) {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    SearchManager.scrollLocked = false;
  }
});
